import { computed, ref } from "vue";
import { getAppGlobals } from "@/plugins/appGlobals";

export const INSTITUTION_SCOPE_LABEL = "Toda a instituição";
export const OWN_UNITS_SCOPE_LABEL = "Minhas unidades";
export const SCOPE_DENIED_FALLBACK_MESSAGE =
  "Ops! Essa unidade não está entre as suas.";

export default function useReportScope() {
  const app = getAppGlobals();

  const units = ref([]);
  const canSelectInstitution = ref(false);
  const selectedUnitId = ref(null);
  const loadingUnits = ref(false);
  const unitsError = ref(false);
  const scopeAlert = ref("");

  const defaultOptionLabel = computed(() =>
    canSelectInstitution.value
      ? INSTITUTION_SCOPE_LABEL
      : OWN_UNITS_SCOPE_LABEL
  );

  const unitOptions = computed(() =>
    units.value.map((unit) => ({
      id: unit.Id,
      isTeam: unit.IsTeam === true,
      name: unit.Name,
      parentPath: buildParentPath(unit),
      fullPath: buildFullPath(unit)
    }))
  );

  function buildParentPath(unit) {
    const path = Array.isArray(unit.Path) ? unit.Path : [];

    return path.map((step) => step.Name).join(" › ");
  }

  function buildFullPath(unit) {
    const parentPath = buildParentPath(unit);

    return parentPath ? `${parentPath} › ${unit.Name}` : unit.Name;
  }

  function authHeader() {
    return {
      Authorization: `bearer ${
        app.$Session.get(app.$userSessionName).token.Value
      }`
    };
  }

  async function loadUnits() {
    loadingUnits.value = true;
    unitsError.value = false;

    try {
      const response = await app.$axios.get("api/report/units", {
        headers: authHeader()
      });
      const data = response.data || {};

      canSelectInstitution.value = data.CanSelectInstitution === true;
      units.value = Array.isArray(data.Units) ? data.Units : [];
    } catch (error) {
      unitsError.value = true;
      canSelectInstitution.value = false;
      units.value = [];
    } finally {
      loadingUnits.value = false;
    }
  }

  function scopedParams(extraParams = {}) {
    const params = { ...extraParams };

    if (selectedUnitId.value) params.unitId = selectedUnitId.value;

    return params;
  }

  function selectUnit(unitId) {
    const parsed = Number(unitId);

    selectedUnitId.value =
      Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    scopeAlert.value = "";
  }

  function isOutOfScopeError(error) {
    return Boolean(error && error.response && error.response.status === 403);
  }

  function resetToDefaultScope(error) {
    selectedUnitId.value = null;
    scopeAlert.value =
      (error && error.response && error.response.data
        ? error.response.data.Message
        : "") || SCOPE_DENIED_FALLBACK_MESSAGE;
  }

  function dismissScopeAlert() {
    scopeAlert.value = "";
  }

  return {
    unitOptions,
    canSelectInstitution,
    defaultOptionLabel,
    selectedUnitId,
    loadingUnits,
    unitsError,
    scopeAlert,
    loadUnits,
    scopedParams,
    selectUnit,
    isOutOfScopeError,
    resetToDefaultScope,
    dismissScopeAlert
  };
}
